import React, { useCallback, useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import api from '../api';

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'finished', label: 'Finished' },
  { key: 'stuck', label: 'Stuck' },
];

const BUCKET_CLASS = {
  finished: 'admin-tag tag-ok',
  stuck: 'admin-tag tag-warn',
  failed: 'admin-tag tag-warn',
  pending: 'admin-tag',
};

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

function DepositRow({ d }) {
  return (
    <tr data-testid={`dep-row-${d.id}`}>
      <td className="admin-mono admin-small">{formatDate(d.created_at)}</td>
      <td className="admin-small">
        <div>{d.user_handle ? `@${d.user_handle}` : '—'}</div>
        <div className="admin-mono admin-muted" data-testid={`dep-email-${d.id}`}>{d.user_email || '—'}</div>
      </td>
      <td style={{ textAlign: 'right' }} className="admin-mono" data-testid={`dep-amount-${d.id}`}>
        ${d.usd_amount.toFixed(2)}
      </td>
      <td>
        {d.pay_currency ? <span className="admin-tag">{d.pay_currency.toUpperCase()}</span> : <span className="admin-muted admin-small">user picks</span>}
        {Number(d.pay_amount) > 0 && <div className="admin-small admin-muted admin-mono">≈ {d.pay_amount}</div>}
      </td>
      <td className="admin-mono admin-small">{d.payment_id || '—'}</td>
      <td data-testid={`dep-status-${d.id}`}>
        <span className={BUCKET_CLASS[d.bucket] || 'admin-tag'}>{d.bucket}</span>
        <div className="admin-small admin-muted" style={{ marginTop: 4 }}>
          NP: {d.status || '—'}{d.manually_credited ? ' · manual' : ''}
        </div>
      </td>
      <td className="admin-mono admin-small">{formatDate(d.updated_at)}</td>
    </tr>
  );
}

function Pager({ page, pages, total, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }} data-testid="dep-pager">
      <button type="button" className="admin-btn-sm" disabled={page <= 1} onClick={() => onChange(page - 1)} data-testid="dep-prev-btn">← Prev</button>
      <span className="admin-small admin-muted" data-testid="dep-page-info">Page {page} of {pages} · {total} deposits</span>
      <button type="button" className="admin-btn-sm" disabled={page >= pages} onClick={() => onChange(page + 1)} data-testid="dep-next-btn">Next →</button>
    </div>
  );
}

export default function AdminDeposits() {
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const res = await api.get('/admin/deposits', { params: { status, page, limit: 25 } });
      setData(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load deposits');
    } finally { setLoading(false); }
  }, [status, page]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const rows = data?.deposits || [];
  const counts = data?.counts || {};

  return (
    <AdminLayout active="deposits">
      <div className="admin-page" data-testid="admin-deposits-page">
        <header className="admin-page-header">
          <h1 className="admin-page-title">Deposits</h1>
          <p className="admin-page-sub">
            Every NowPayments crypto top-up, newest first. Stuck = user paid on-chain but the account was never credited.
          </p>
        </header>

        <div className="admin-toolbar">
          <div className="admin-chip-row">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                className={`admin-chip${status === t.key ? ' active' : ''}`}
                onClick={() => { setStatus(t.key); setPage(1); }}
                data-testid={`dep-tab-${t.key}`}
              >
                {t.label}
                {counts[t.key] !== undefined && <span className="admin-chip-count">{counts[t.key]}</span>}
              </button>
            ))}
          </div>
          <button type="button" className="admin-btn-sm" onClick={fetchList} data-testid="dep-refresh-btn">Refresh</button>
        </div>

        {err && <div className="admin-error" data-testid="dep-error">{err}</div>}
        {loading && <div className="admin-loading">Loading…</div>}

        {!loading && !err && (rows.length === 0 ? (
          <div className="admin-empty" data-testid="admin-dep-empty">
            {status === 'all' ? 'No deposits yet.' : `No ${status} deposits.`}
          </div>
        ) : (
          <>
            <div className="admin-table-wrap">
              <table className="admin-table" data-testid="admin-dep-table">
                <thead>
                  <tr>
                    <th>Created</th>
                    <th>User</th>
                    <th style={{ textAlign: 'right' }}>USD</th>
                    <th>Crypto</th>
                    <th>Payment ID</th>
                    <th>Status</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>{rows.map((d) => <DepositRow key={d.id} d={d} />)}</tbody>
              </table>
            </div>
            <Pager page={data.page} pages={data.pages} total={data.total} onChange={setPage} />
          </>
        ))}
      </div>
    </AdminLayout>
  );
}
